import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'security',
  slug: 'security',
  name: 'Security',
  description: 'Vulnerability scan ingestion, breach and credential monitoring, phishing-simulation campaigns, compliance framework scoring, and a read-only self-audit of the platform\'s own security posture.',
  icon: 'Shield',
  endpoints: [
    {
      slug: 'vulnscan-list',
      method: 'GET',
      path: '/api/vulnscan',
      summary: 'List vulnerability findings',
      description: 'Returns network/infrastructure vulnerability findings for the tenant, sorted critical-first. Findings are created by a scanner (OpenVAS/Greenbone, Nessus, Qualys, Nmap scripts, etc.) posting to the keyed ingest webhook, or by a manual admin import; duplicate findings (same host + CVE/name) are deduplicated and re-counted rather than re-created.',
      auth: 'session',
      params: [
        { name: 'status', in: 'query', type: 'string', description: 'Filter to a single status, e.g. open or resolved.' },
        { name: 'severity', in: 'query', type: 'string', description: 'Filter to a single severity: critical, high, medium, low, or info.' },
      ],
      responseExample: `[
  {
    "id": "vuln_7a1c",
    "host": "10.20.4.31",
    "name": "OpenSSH < 9.3 multiple CVEs",
    "cve": "CVE-2023-38408",
    "severity": "high",
    "cvss": "8.1",
    "port": "22",
    "description": "The PKCS#11 feature in ssh-agent in OpenSSH before 9.3 allows...",
    "solution": "Upgrade OpenSSH to 9.3 or later.",
    "source": "openvas",
    "status": "open",
    "count": 3,
    "firstSeen": "2026-07-02T06:11:00.000Z",
    "lastSeen": "2026-07-19T06:14:22.000Z"
  }
]`,
      sourceFile: 'routes/vulnscan.js',
    },
    {
      slug: 'vulnscan-resolve',
      method: 'POST',
      path: '/api/vulnscan/:id/resolve',
      summary: 'Resolve a vulnerability finding',
      description: 'Marks a finding resolved and stamps resolvedAt. Resolved findings are excluded from the open counts in the summary endpoint and will be re-opened (a new finding with a new id) if the same host/CVE reappears on a later scan.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Vulnerability finding id.' }],
      responseExample: `{ "ok": true }`,
      sourceFile: 'routes/vulnscan.js',
    },
    {
      slug: 'breach-list',
      method: 'GET',
      path: '/api/breach',
      summary: 'List breach findings',
      description: 'Returns up to the 1,000 most recent breach findings for the tenant\'s monitored domain, newest first. Findings come from Have I Been Pwned (HIBP) domain-breach scans, either scheduled or triggered manually.',
      auth: 'session',
      responseExample: `[
  {
    "id": "brch_44e2",
    "key": "jklein@example.org|Collection #1",
    "email": "jklein@example.org",
    "breach": "Collection #1",
    "foundAt": "2026-07-15T09:00:04.000Z",
    "status": "open"
  }
]`,
      sourceFile: 'routes/breach.js',
    },
    {
      slug: 'breach-scan',
      method: 'POST',
      path: '/api/breach/scan',
      summary: 'Run a breach scan now',
      description: 'Triggers an immediate Have I Been Pwned domain-breach lookup for the tenant\'s configured domain (requires an HIBP API key and a domain verified in that HIBP account). New accounts found in a breach raise a high-severity Security alert; already-seen breach/email pairs are skipped.',
      auth: 'session',
      responseExample: `{ "ok": true, "accounts": 2, "newFindings": 2 }`,
      sourceFile: 'routes/breach.js',
    },
    {
      slug: 'breach-password-check',
      method: 'POST',
      path: '/api/breach/password-check',
      summary: 'Check a password against Pwned Passwords',
      description: 'Checks a candidate password against the Have I Been Pwned Pwned Passwords range API using k-anonymity: only the first 5 characters of the password\'s SHA-1 hash are sent, so the password itself never leaves the server as plaintext and no API key is required.',
      auth: 'session',
      requestBodyExample: `{ "password": "Summer2026!" }`,
      responseExample: `{ "pwned": true, "count": 41837 }`,
      sourceFile: 'routes/breach.js',
    },
    {
      slug: 'phishing-create',
      method: 'POST',
      path: '/api/phishing',
      summary: 'Create a phishing simulation campaign',
      description: 'Creates a draft phishing-awareness campaign with a subject, sender name, HTML body (a {{link}} placeholder is replaced per-recipient with a unique tracked click link), and a landing-page teach-back message. Recipients can be listed explicitly or pulled from every staff member with an email on file via useAllStaff. The campaign is not sent until the send endpoint is called.',
      auth: 'session',
      requestBodyExample: `{
  "name": "Q3 password-reset lure",
  "subject": "Action required: verify your account",
  "fromName": "IT Department",
  "body": "<p>Please <a href=\\"{{link}}\\">review your account</a>.</p>",
  "landingMessage": "This was a simulated phishing test from your IT department.",
  "useAllStaff": true
}`,
      responseExample: `{
  "id": "phish_2f90",
  "name": "Q3 password-reset lure",
  "subject": "Action required: verify your account",
  "fromName": "IT Department",
  "status": "draft",
  "createdAt": "2026-07-20T09:02:11.000Z",
  "sentAt": "",
  "total": 48,
  "sent": 0,
  "clicked": 0,
  "clickRate": 0
}`,
      sourceFile: 'routes/phishing.js',
    },
    {
      slug: 'phishing-send',
      method: 'POST',
      path: '/api/phishing/:id/send',
      summary: 'Send a phishing simulation campaign',
      description: 'Sends the campaign email to every recipient over the tenant\'s configured SMTP, with each recipient\'s link pointing to a unique token so clicks are attributed individually. Marks the campaign sent and records per-send failures without aborting the batch.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Phishing campaign id.' }],
      responseExample: `{ "ok": true, "sent": 47, "failed": 1 }`,
      sourceFile: 'routes/phishing.js',
    },
    {
      slug: 'compliance-summary',
      method: 'GET',
      path: '/api/compliance',
      summary: 'Get compliance framework scores',
      description: 'Returns a score summary for every supported compliance framework (SOC 2, ISO 27001, HIPAA, PCI, FERPA/COPPA, NIST CSF, and CIS), each with a percentage score derived from control assessments and a breakdown of met/partial/not-met/N/A/unassessed control counts.',
      auth: 'session',
      responseExample: `[
  {
    "id": "ferpa",
    "name": "FERPA / COPPA",
    "blurb": "Student education records and children's online privacy.",
    "score": 78,
    "counts": { "met": 21, "partial": 4, "not": 2, "na": 1, "unassessed": 0, "total": 28 }
  }
]`,
      sourceFile: 'routes/compliance.js',
    },
    {
      slug: 'security-audit',
      method: 'GET',
      path: '/api/admin/security-audit',
      summary: 'Run the platform security self-audit',
      description: 'Read-only inspection of this deployment\'s own security posture: HSTS/CSP/session-cookie headers, admin two-factor enforcement, presence of APP_SECRET_KEY/SESSION_SECRET, login rate-limiting and lockout, read-only role enforcement, and RBAC grant coverage. It never mutates state and never returns a secret value, only whether one is set.',
      auth: 'session',
      responseExample: `{
  "score": { "passed": 9, "total": 11 },
  "checks": [
    {
      "key": "hsts",
      "label": "HTTP Strict Transport Security (HSTS)",
      "status": "pass",
      "detail": "Strict-Transport-Security is sent (max-age=15552000; includeSubDomains) because connections are served/treated as HTTPS.",
      "recommendation": ""
    },
    {
      "key": "admin_2fa",
      "label": "Admin two-factor enforcement",
      "status": "warn",
      "detail": "Admin accounts are not required to enroll in two-factor authentication.",
      "recommendation": "Enable \\"require admin 2FA\\" in security settings (POST /api/security-settings require2faAdmins=true) so every admin must enroll TOTP."
    }
  ],
  "generatedAt": "2026-07-20T09:05:00.000Z"
}`,
      sourceFile: 'routes/securityaudit.js',
    },
  ],
};
